import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, ArrowLeft, Search, FileSpreadsheet, Printer, TrendingUp, TrendingDown } from 'lucide-react';
import Layout from '../../../components/Layout';
import { inventoryReportService, StockAdjustmentReportDTO } from '../../../services/inventoryReportService';
import { exportToCsv, printReport } from '../../../utils/exportUtils';
import './InventoryReportCommon.css';

const fmtQty  = (n: number) => new Intl.NumberFormat().format(n);
const fmtDate = (d: string) => new Date(d).toLocaleString();
const today   = () => new Date().toISOString().split('T')[0];
const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; };

const StockAdjustmentReportPage: React.FC = () => {
  const navigate  = useNavigate();
  const [startDate,   setStartDate]   = useState(monthAgo());
  const [endDate,     setEndDate]     = useState(today());
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [items,       setItems]       = useState<StockAdjustmentReportDTO[]>([]);

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await inventoryReportService.getAdjustments({ startDate, endDate });
      const d: StockAdjustmentReportDTO[] = (res.data as any)?.data ?? res.data ?? [];
      setItems(d);
    } catch { setItems([]); }
    finally { setSearching(false); }
  };

  const handleExcel = () => {
    exportToCsv(
      `stock-adjustments-${startDate}_${endDate}.csv`,
      ['Doc #', 'Date', 'Product', 'SKU', 'Warehouse', 'Previous Qty', 'New Qty', 'Change', 'Reason', 'Notes', 'By'],
      items.map(i => [
        i.documentNumber, fmtDate(i.createdAt), i.productName, i.productSKU,
        i.warehouseName, i.previousQuantity, i.newQuantity, i.quantityChanged,
        i.reason, i.notes ?? '', i.createdByName ?? '',
      ]),
    );
  };

  const positive = items.filter(i => i.quantityChanged > 0).reduce((s, i) => s + i.quantityChanged, 0);
  const negative = items.filter(i => i.quantityChanged < 0).reduce((s, i) => s + Math.abs(i.quantityChanged), 0);

  return (
    <Layout>
      <div className="irp-page">

        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title"><SlidersHorizontal size={22} strokeWidth={1.8} /> Stock Adjustment Report</h1>
            <p className="irp-hero-sub">Audit trail of all manual stock adjustments with reasons</p>
          </div>
          <div className="irp-hero-actions">
            {hasSearched && items.length > 0 && (
              <div className="irp-export-group">
                <button className="irp-export-btn irp-export-excel" onClick={handleExcel}><FileSpreadsheet size={13} /> Export Excel</button>
                <button className="irp-export-btn irp-export-pdf" onClick={printReport}><Printer size={13} /> Print / PDF</button>
              </div>
            )}
            <button className="irp-back-btn" onClick={() => navigate('/reports/inventory')}><ArrowLeft size={14} /> Back</button>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title"><Search size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />Date Range Filter</span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="irp-filter-bar">
              <div className="irp-filter-group">
                <span className="irp-filter-label">From Date</span>
                <input type="date" className="ims-form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="irp-filter-group">
                <span className="irp-filter-label">To Date</span>
                <input type="date" className="ims-form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
              <button className="ims-btn ims-btn-primary" onClick={handleSearch} disabled={searching} style={{ alignSelf: 'flex-end' }}>
                {searching ? <><span className="irp-spinner" /> Searching…</> : <><Search size={14} /> Search</>}
              </button>
            </div>
          </div>
        </div>

        {!hasSearched && (
          <div className="irp-prompt"><SlidersHorizontal size={48} strokeWidth={1} /><p>Set a date range and click <strong>Search</strong>.</p></div>
        )}

        {hasSearched && !searching && (
          <>
            <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-total"><SlidersHorizontal size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{items.length}</span>
                  <span className="ims-stat-label">Total Adjustments</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-value"><TrendingUp size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value irp-positive">+{fmtQty(positive)}</span>
                  <span className="ims-stat-label">Positive Adj.</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-out"><TrendingDown size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value irp-negative">-{fmtQty(negative)}</span>
                  <span className="ims-stat-label">Negative Adj.</span>
                </div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="ims-card"><div className="irp-empty">No adjustments found for the selected period.</div></div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <SlidersHorizontal size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    Adjustments ({items.length} records)
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="irp-table">
                    <thead>
                      <tr>
                        <th>Doc #</th>
                        <th>Date</th>
                        <th>Product</th>
                        <th>SKU</th>
                        <th>Warehouse</th>
                        <th className="num">Previous Qty</th>
                        <th className="num">New Qty</th>
                        <th className="num">Change</th>
                        <th>Reason</th>
                        <th>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.id}>
                          <td><span className="irp-doc-num">{item.documentNumber}</span></td>
                          <td style={{ fontSize: '0.78rem', color: 'var(--ims-text-sub)', whiteSpace: 'nowrap' }}>{fmtDate(item.createdAt)}</td>
                          <td style={{ fontWeight: 500 }}>{item.productName}</td>
                          <td><span className="irp-sku">{item.productSKU}</span></td>
                          <td>{item.warehouseName}</td>
                          <td className="num">{fmtQty(item.previousQuantity)}</td>
                          <td className="num">{fmtQty(item.newQuantity)}</td>
                          <td className={`num ${item.quantityChanged > 0 ? 'irp-positive' : item.quantityChanged < 0 ? 'irp-negative' : ''}`}>
                            {item.quantityChanged > 0 ? `+${fmtQty(item.quantityChanged)}` : fmtQty(item.quantityChanged)}
                          </td>
                          <td style={{ fontSize: '0.82rem' }}>{item.reason}</td>
                          <td style={{ fontSize: '0.78rem', color: 'var(--ims-text-sub)' }}>{item.createdByName ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default StockAdjustmentReportPage;
